import React, { useState, useMemo, useEffect } from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  TextField,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import CustomAppBar from "../../components/AppBar";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../globals/axios";
import CircularProgress from "@mui/material/CircularProgress";
import { getOrderDetailsFields } from "../../Data/fieldData";
import AddressForm from "../../components/Custom/AddressForm";
import { biocharOrderPlaceValidationSchema } from "../../components/Custom/ValidationSchema";
import {
  handleMobileKeyDown,
  transformBackendErrors,
} from "../../globals/utils";
import useFormValidation from "../../components/Custom/FormValidation";
import GenericButton from "../../components/Custom/GenericButton";
import { useAlert } from "../../components/Alert";

const BiocharOrderPlace = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [orderType, setOrderType] = useState(
    location.state?.orderType || "farmer"
  );

  const [farmers, setFarmers] = useState([]);
  const [farms, setFarms] = useState([]);
  const [plants, setPlants] = useState([]);
  const [farmerLoading, setFarmerLoading] = useState(false);
  const [farmLoading, setFarmLoading] = useState(false);
  const [formData, setFormData] = useState({
    address_line1: "",
    commission: "",
    cost_per_kg: "",
    delivery_charges: "",
    district: "",
    is_self_pickup: false,
    packaging_charges: "0",
    payment_mode: "DIGITAL",
    pincode: "",
    postoffice: "",
    requested_quantity: "",
    state: "",
    village: "",
    source_plant: null,
  });
  const totalAmount = useMemo(() => {
    const quantityNum = parseFloat(formData.requested_quantity) || 0;
    const costPerKgNum = parseFloat(formData.cost_per_kg) || 0;
    const deliveryChargesNum = parseFloat(formData.delivery_charges) || 0;
    const packagingChargesNum = parseFloat(formData.packaging_charges) || 0;
    const commissionNum = parseFloat(formData.commission) || 0;

    const total =
      costPerKgNum * quantityNum +
      deliveryChargesNum +
      packagingChargesNum +
      commissionNum;

    // Auto-switch to digital payment if cash payment exceeds limit
    if (formData.payment_mode === "CASH" && total > 15000) {
      setFormData((prev) => ({ ...prev, payment_mode: "DIGITAL" }));
    }

    return total;
  }, [
    formData.requested_quantity,
    formData.cost_per_kg,
    formData.delivery_charges,
    formData.packaging_charges,
    formData.commission,
    formData.payment_mode,
  ]);

  const orderDetailsFields = getOrderDetailsFields(t);
  const { errors, validate, setErrors } = useFormValidation(
    formData,
    biocharOrderPlaceValidationSchema(orderType)
  );

  const { showAlert } = useAlert();

  useEffect(() => {
    const fetchFarmers = async () => {
      setFarmerLoading(true);
      try {
        const response = await api.get(
          "/usermanagement/get-sub-users/?farmers&paginate=false"
        );
        // The structure is { data: { farmers: [...] } }
        if (response.data && response.data.data && response.data.data.farmers) {
          setFarmers(response.data.data.farmers || []);
        } else {
          console.error(
            "Unexpected farmers API response structure:",
            response.data
          );
          setFarmers([]);
        }
      } catch (err) {
        console.error("Error fetching farmers:", err);
        showAlert("error", "Failed to load farmers list");
        setFarmers([]);
      } finally {
        setFarmerLoading(false);
      }
    };

    if (orderType === "farmer") {
      fetchFarmers();
    }
  }, [orderType]);

  useEffect(() => {
    const fetchplants = async () => {
      try {
        const response = await api.get("/biochar/get-biochar-plants/");
        if (response.data) {
          setPlants(response.data || []);
        }
      } catch (err) {
        console.error("Error fetching plants:", err);
        showAlert("error", "Failed to load plants list");
        setPlants([]);
      }
    };
    fetchplants();
  }, []);

  // Fetch farms when farmer is selected
  const fetchFarms = async (farmer_id) => {
    if (!farmer_id) {
      setFarms([]);
      return;
    }

    setFarmLoading(true);
    try {
      const response = await api.get(`/farmer/get-farmer-farms/${farmer_id}/`);
      if (response.data && response.data.data && response.data.data.farms) {
        setFarms(response.data.data.farms || []);
      } else {
        console.error(
          "Unexpected farms API response structure:",
          response.data
        );
        setFarms([]);
      }
    } catch (err) {
      console.error("Error fetching farms:", err);
      showAlert("error", "Failed to load farms list");
      setFarms([]);
    } finally {
      setFarmLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const numericFields = [
      "requested_quantity",
      "cost_per_kg",
      "delivery_charges",
      "packaging_charges",
      "commission",
    ];

    if (numericFields.includes(name)) {
      setFormData((prev) => ({ ...prev, [name]: value }));

      return;
    }

    if (name === "farmer_id" && value) {
      fetchFarms(value);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => {
      if (name === "is_self_pickup" && checked) {
        return { ...prev, [name]: checked, delivery_charges: 0 };
      }
      return { ...prev, [name]: checked };
    });
  };

  const updateAddressData = (newData) => {
    setFormData((prev) => ({ ...prev, ...newData }));
  };

  const handleFieldChange = (field, value) => {
    updateAddressData({ [field]: value });
  };

  const handleCustomerTypeChange = (e) => {
    setOrderType(e.target.value);
    setErrors({});
  };

  const prepareSubmitData = () => {
    if (orderType === "farmer") {
      return {
        ...formData,
        order_type: "FARMER",
        farmer_id: formData.farmer_id,
        farm_id: formData.farm_id,
      };
    } else {
      return {
        ...formData,
        order_type: "INSTITUTION",
        institution_name: formData.institution_name,
        institution_phone_number: formData.institution_phone_number,
      };
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const orderData = prepareSubmitData();

      // Validate cash payment amount limit
      if (orderData.payment_mode === "CASH" && totalAmount > 15000) {
        showAlert(
          "error",
          "Cash payment orders cannot exceed ₹15,000. Please choose digital payment for amounts above ₹15,000."
        );
        return;
      }

      let errors = await validate(orderData);
      if (errors && Object.keys(errors).length > 0) {
        showAlert("error", "Fix Validation Errors");
        return;
      }

      await api.post("/biochar/biochar-order/", orderData);
      showAlert("success", "Order placed successfully!");
      setTimeout(() => {
        navigate("/biochar-orders-list", {
          state: {
            orderType: orderType === "farmer" ? "FARMER" : "INSTITUTION",
          },
        });
      }, 1500);
    } catch (error) {
      if (error.response) {
        const rawBackendErrors = transformBackendErrors(error.response.data);
        const formattedErrors = Object.entries(rawBackendErrors).map(
          ([field, message]) => {
            const readableField = field
              .replace(/_/g, " ")
              .replace(/\b\w/g, (char) => char.toUpperCase());
            return `${readableField}: ${message}`;
          }
        );
        showAlert("error", formattedErrors);
      } else {
        showAlert(
          "error",
          `Error: ${
            error.message || "Failed to create order. Please try again."
          }`
        );
      }
    }
  };

  return (
    <>
      <CustomAppBar />
      <Container maxWidth="xs">
        <Box sx={{ mt: 3, mb: 4 }}>
          <CustomTitle>{t("Place an Order")}</CustomTitle>

          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Add customer type dropdown */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>{t("Customer Type")}</InputLabel>
                    <Select
                      value={orderType}
                      label={t("Customer Type")}
                      onChange={handleCustomerTypeChange}
                      required
                    >
                      <MenuItem value="farmer">{t("Farmer")}</MenuItem>
                      <MenuItem value="institution">
                        {t("Institution")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth error={Boolean(errors.plant_id)}>
                    <InputLabel>{t("Source Plant")}</InputLabel>
                    <Select
                      name="source_plant"
                      value={formData.source_plant}
                      label={t("Source Plant")}
                      onChange={handleInputChange}
                      required
                      disabled={farmerLoading}
                    >
                      {plants.length === 0 ? (
                        <MenuItem disabled>
                          {t("No Source Plant available")}
                        </MenuItem>
                      ) : (
                        plants.map((plant) => (
                          <MenuItem key={plant?.id} value={plant?.id}>
                            {plant.name || `Plant #${plant?.id}`}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {errors.source_plant && (
                      <FormHelperText>{errors.source_plant}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                {orderType === "farmer" ? (
                  <>
                    <Grid item xs={12}>
                      <FormControl fullWidth error={Boolean(errors.farmer_id)}>
                        <InputLabel>{t("Farmer")}</InputLabel>
                        <Select
                          name="farmer_id"
                          value={formData.farmer_id}
                          label={t("Farmer")}
                          onChange={handleInputChange}
                          required
                          disabled={farmerLoading}
                        >
                          {farmerLoading ? (
                            <MenuItem disabled>
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                              {t("Loading...")}
                            </MenuItem>
                          ) : farmers.length === 0 ? (
                            <MenuItem disabled>
                              {t("No farmers available")}
                            </MenuItem>
                          ) : (
                            farmers.map((farmer) => (
                              <MenuItem key={farmer?.id} value={farmer?.id}>
                                {farmer.full_name || `Farmer #${farmer?.id}`}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        {errors.farmer_id && (
                          <FormHelperText>{errors.farmer_id}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth error={Boolean(errors.farm_id)}>
                        <InputLabel>{t("Farm")}</InputLabel>
                        <Select
                          name="farm_id"
                          value={formData.farm_id}
                          label={t("Farm")}
                          onChange={handleInputChange}
                          required
                          disabled={!formData.farmer_id || farmLoading}
                        >
                          {farmLoading ? (
                            <MenuItem disabled>
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                              {t("Loading...")}
                            </MenuItem>
                          ) : !formData.farmer_id ? (
                            <MenuItem disabled>
                              {t("Select a farmer first")}
                            </MenuItem>
                          ) : farms.length === 0 ? (
                            <MenuItem disabled>
                              {t("No farms available")}
                            </MenuItem>
                          ) : (
                            farms.map((farm) => (
                              <MenuItem key={farm.id} value={farm.id}>
                                {farm.farm_name || `Farm #${farm.id}`}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        {errors.farm_id && (
                          <FormHelperText>{errors.farm_id}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        name="institution_name"
                        label={t("Institution Name")}
                        value={formData.institution_name}
                        onChange={handleInputChange}
                        required
                        fullWidth
                        error={Boolean(errors.institution_name)}
                        helperText={errors.institution_name || ""}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="institution_phone_number"
                        label={t("Institution Phone Number")}
                        value={formData.institution_phone_number}
                        onChange={handleInputChange}
                        required
                        fullWidth
                        error={Boolean(errors.institution_phone_number)}
                        helperText={errors.institution_phone_number || ""}
                        inputProps={{
                          maxLength: 10,
                          inputMode: "numeric",
                        }}
                        onKeyDown={handleMobileKeyDown}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              +91
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </>
                )}
                {orderDetailsFields.map((field) => (
                  <Grid item xs={12} key={field.name}>
                    <TextField
                      key={field.name}
                      name={field.name}
                      label={field.label}
                      type={field.type}
                      value={formData[field.name]}
                      onChange={handleInputChange}
                      required
                      error={Boolean(errors[field.name])}
                      helperText={errors[field.name] || ""}
                      fullWidth
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="is_self_pickup"
                        checked={formData.is_self_pickup}
                        onChange={handleCheckboxChange}
                        color="primary"
                      />
                    }
                    label={t("Self Pickup")}
                  />
                </Grid>{" "}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>{t("Payment Mode")}</InputLabel>
                    <Select
                      name="payment_mode"
                      value={formData.payment_mode}
                      label={t("Payment Mode")}
                      onChange={handleInputChange}
                      required
                    >
                      <MenuItem value="DIGITAL">{t("Digital")}</MenuItem>
                      <MenuItem value="CASH" disabled={totalAmount > 15000}>
                        {t("Cash")}
                        {totalAmount > 15000 && " (Limit Exceeded)"}
                      </MenuItem>
                    </Select>
                    {totalAmount > 15000 &&
                      formData.payment_mode === "CASH" && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 1 }}
                        >
                          {t(
                            "Cash payment automatically switched to digital due to amount exceeding ₹15,000 limit."
                          )}
                        </Typography>
                      )}
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    {t("Delivery Address")}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <AddressForm
                    handleAddressChange={handleInputChange}
                    showAllFields={true}
                    t={t}
                    handleFieldChange={handleFieldChange}
                    profileData={{
                      pincode: formData.pincode,
                      state: formData.state,
                      district: formData.district,
                      postoffice: formData.postoffice,
                      address_line1: formData.address_line1,
                    }}
                    fieldKeyMap={{
                      pin_code: "pincode",
                      state: "state",
                      district: "district",
                      post_office: "postoffice",
                      address: "address_line1",
                    }}
                    updateProfileData={updateAddressData}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="village"
                    label={t("Village")}
                    value={formData.village}
                    onChange={handleInputChange}
                    required={!formData.selfPickup}
                    error={Boolean(errors.village)}
                    helperText={errors.village || ""}
                    fullWidth
                  />
                </Grid>{" "}
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {t("Total Amount Payable")}
                    </Typography>
                    <Typography
                      variant="h5"
                      color={
                        formData.payment_mode === "CASH" && totalAmount > 15000
                          ? "error"
                          : "primary"
                      }
                      fontWeight="bold"
                    >
                      Rs. {totalAmount.toFixed(2)}
                    </Typography>

                    {/* Cash payment amount warning */}
                    {formData.payment_mode === "CASH" && (
                      <Box sx={{ mt: 1 }}>
                        {totalAmount > 15000 ? (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              {t(
                                "Cash payment limit exceeded! Maximum allowed: ₹15,000. Please switch to digital payment."
                              )}
                            </Typography>
                          </Alert>
                        ) : totalAmount > 12000 ? (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              {t(
                                "Amount is approaching cash payment limit of ₹15,000"
                              )}
                            </Typography>
                          </Alert>
                        ) : (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              {t("Cash payment limit: ₹15,000")}
                            </Typography>
                          </Alert>
                        )}
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Alert severity="info">
                    <Typography variant="body2">
                      {t(
                        "Disclaimer: Orders cannot be cancelled once placed. Payment terms are as per company policy."
                      )}
                    </Typography>
                  </Alert>
                </Grid>{" "}
                <Grid item xs={12}>
                  <GenericButton
                    backgroundColor={
                      formData.payment_mode === "CASH" && totalAmount > 15000
                        ? "#ccc"
                        : "#4CAF50"
                    }
                    fontColor={"#fff"}
                    fontSize={"14px"}
                    padding={"10px"}
                    onClick={handleSubmit}
                    disabled={
                      formData.payment_mode === "CASH" && totalAmount > 15000
                    }
                  >
                    +{t("Place Order")}
                  </GenericButton>

                  <GenericButton
                    backgroundColor="#d32f2f"
                    fontColor={"#fff"}
                    fontSize={"14px"}
                    padding={"10px"}
                    onClick={() => navigate("/biochar-orders-list")}
                  >
                    {t("Cancel")}
                  </GenericButton>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default BiocharOrderPlace;
